/* @flow */

import React from 'react';
import moment from 'moment';

import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import InputLabel from '@material-ui/core/InputLabel';
import MenuItem from '@material-ui/core/MenuItem';
import Select from '@material-ui/core/Select';

import AmountFormatterComponent from 'components/formatters/amount';
import ButtonComponent from 'components/buttons/button';
import CompanyItemComponent from 'components/company';
import IntlMessageComponent from 'components/formatters/intlMessage';
import MandatoryFormFieldsComponent from 'components/form/mandatory';
import PageSubTitleViewComponent from 'components/pageTitle/subTitle';
import UploadFieldComponent from 'components/formFields/upload';

import Document from 'models/documents/document';
import DocumentType from 'models/documents/documentType';

import DocumentService from 'services/DocumentService';
import { handleApiFormResponse } from 'lib/forms';

import {
  INVOICE_SELLER_DOCUMENT_DELETE_POST,
  INVOICE_SELLER_DOCUMENT_POST
} from 'constants/apiRoutes';

const FIELD_WIDTH = 260;

class SellInvoiceDocumentsBatchComponent extends React.Component {

  constructor(props) {
    super(props);

    const entries = props.batch.map((batchEntry) => {
      const documents = props.requiredDocumentTypes.map(docType => new Document(docType, true));

      // Match already-provided documents to this entry by stored name === invoice reference
      props.providedDocuments
        .filter(pd => (pd.name || '') === batchEntry.reference)
        .forEach((pd) => {
          const found = documents.find(doc => doc.type.typeId === pd.typeId);
          if (found !== undefined) {
            found.id = pd.id;
            found.filename = pd.filename;
          } else {
            const document = new Document(new DocumentType(pd.typeId, pd.code), false);
            document.id = pd.id;
            document.filename = pd.filename;
            documents.push(document);
          }
        });

      return {
        batch: batchEntry,
        documents,
        filteredExtraDocuments: [...props.extraDocuments],
        selectedDocumentType: null
      };
    });

    this.state = {
      isProcessing: false,
      entries
    };
  }

  handleDocumentSelect = (entryIndex: number, docIndex: number) => (file: File) => {
    const entries = this.state.entries.slice();
    const doc = entries[entryIndex].documents[docIndex];
    doc.file = file;
    doc.filename = file.name;
    doc.isDeleted = false;
    doc.isMissing = false;
    this.setState({ entries });
  };

  handleDocumentRemove = (entryIndex: number, docIndex: number) => () => {
    const entries = this.state.entries.slice();
    const entry = entries[entryIndex];
    const doc = entry.documents[docIndex];

    if (doc.isRequired) {
      doc.file = null;
      doc.filename = '';
      doc.isDeleted = true;
    } else if (doc.id > 0) {
      doc.isDeleted = true;
    } else {
      const foundType = this.props.extraDocuments.find(d => d.type.typeId === doc.type.typeId);
      if (foundType !== undefined) entry.filteredExtraDocuments.push(foundType);
      entry.documents.splice(docIndex, 1);
    }
    this.setState({ entries });
  };

  handleDocumentTypeChange = (entryIndex: number) => (event) => {
    const entries = this.state.entries.slice();
    entries[entryIndex].selectedDocumentType = event.target.value;
    this.setState({ entries });
  };

  addDocument = (entryIndex: number) => () => {
    const entries = this.state.entries.slice();
    const entry = entries[entryIndex];
    const selected = entry.selectedDocumentType;
    if (!selected) return;

    entry.documents.push(new Document(selected, false));
    if (selected.typeId !== this.props.toClassifyDocumentType.typeId) {
      entry.filteredExtraDocuments = entry.filteredExtraDocuments.filter(d => d.type !== selected);
      entry.selectedDocumentType = entry.filteredExtraDocuments.length ? entry.filteredExtraDocuments[0].type : null;
    }
    this.setState({ entries });
  };

  validate = () => {
    let isValid = true;
    const entries = this.state.entries.slice();

    entries.forEach((entry) => {
      entry.documents.forEach((doc) => {
        if (doc.isRequired) {
          if (doc.id === 0 && doc.file === null) { doc.isMissing = true; isValid = false; }
          if (doc.id !== 0 && doc.isDeleted) { doc.isMissing = true; isValid = false; }
        } else if (doc.id === 0 && doc.file === null) {
          doc.isMissing = true; isValid = false;
        }
      });
    });

    if (!isValid) this.setState({ entries });
    return isValid;
  };

  submit = async () => {
    if (!this.validate()) return;

    const delPromises = [];
    const addPromises = [];

    this.state.entries.forEach((entry) => {
      entry.documents.forEach((doc) => {
        if (!doc.isRequired && doc.isDeleted && doc.id > 0) {
          delPromises.push(DocumentService.deleteDocument(INVOICE_SELLER_DOCUMENT_DELETE_POST, doc.id));
        }
        if (!doc.isDeleted && doc.file !== null) {
          addPromises.push(DocumentService.sendDocument2(INVOICE_SELLER_DOCUMENT_POST, doc.file,
            { documentTypeId: doc.type.typeId, invoiceId: this.props.invoiceId, documentName: entry.batch.reference }));
        }
      });
    });

    if (delPromises.length === 0 && addPromises.length === 0) {
      this.props.successHandler();
      return;
    }

    this.setState({ isProcessing: true });

    try {
      await Promise.all(delPromises);
      await Promise.all(addPromises);
      this.props.successHandler();
    } catch (e) {
      handleApiFormResponse.call(this, e);
    }
  };

  cancelInvoice = () => {
    this.setState({ isProcessing: true });
    this.props.cancelInvoice();
  };

  render() {
    const defaultFieldOptions = { margin: 'normal', fullWidth: true };
    const debtor = this.props.debtorCompany;

    return (
      <React.Fragment>
        <PageSubTitleViewComponent id="page.sellInvoice.tab4.title" />

        {debtor &&
          <Box mt={3} mb={2}>
            <Box px={4} py={3} bgcolor="lightblue.main">
              <CompanyItemComponent
                company={debtor}
                selected
                hideVATCountry={/[a-z]/i.test(debtor.vatNumber || '')}
              />
            </Box>
          </Box>
        }

        {this.state.entries.map((entry, entryIndex) => (
          <Box mt={4} key={`entry-${entryIndex}`}>
            <Box p={2} mb={1} display="flex" flexWrap="wrap" style={{ backgroundColor: '#f5f5f5' }}>
              <Box mr={5}>
                <strong><IntlMessageComponent id="page.sellInvoice.tab4.batch.invoice" values={{ number: entryIndex + 1, reference: entry.batch.reference }} /></strong>
              </Box>
              <Box mr={5}>
                <IntlMessageComponent id="page.sellInvoice.tab4.batch.dueDate" />{' '}
                {entry.batch.dueDate ? moment(entry.batch.dueDate).format('DD/MM/YYYY') : ''}
              </Box>
              <Box>
                <IntlMessageComponent id="page.sellInvoice.tab4.batch.invoiceTotal" />{' '}
                <AmountFormatterComponent amount={entry.batch.debtAmount} currency="EUR" minimumFractionDigits={0} maximumFractionDigits={2} />
              </Box>
            </Box>

            <Box display="flex" flexWrap="wrap" alignItems="flex-start">
              {entry.documents.map((doc, docIndex) => {
                if (!doc.isRequired && doc.isDeleted) return null;
                return (
                  <Box key={`doc-${entryIndex}-${docIndex}`} mr={3} style={{ width: FIELD_WIDTH }}>
                    <UploadFieldComponent
                      disabled={this.state.isProcessing}
                      displayProps={defaultFieldOptions}
                      filename={doc.filename}
                      isMissing={doc.isMissing}
                      isRequired={doc.isRequired}
                      label={`document.type.${doc.type.code}`}
                      successHandler={this.handleDocumentSelect(entryIndex, docIndex)}
                      removeHandler={this.handleDocumentRemove(entryIndex, docIndex)}
                    />
                  </Box>
                );
              })}

              <Box style={{ width: FIELD_WIDTH }} mt={2}>
                <FormControl fullWidth>
                  <InputLabel><IntlMessageComponent id="component.company.documentList.header.documenttype.optional" /></InputLabel>
                  <Select
                    value={entry.selectedDocumentType}
                    onChange={this.handleDocumentTypeChange(entryIndex)}
                  >
                    {entry.filteredExtraDocuments.map((doc) => (
                      <MenuItem key={doc.type.typeId} value={doc.type}><IntlMessageComponent id={`document.type.${doc.type.code}`} /></MenuItem>
                    ))}
                  </Select>
                  <Box mt={1}>
                    <ButtonComponent
                      color="secondary"
                      variant="contained"
                      disabled={this.state.isProcessing || entry.selectedDocumentType === null}
                      onClick={this.addDocument(entryIndex)}
                    ><IntlMessageComponent id="general.button.addDocument" /></ButtonComponent>
                  </Box>
                </FormControl>
              </Box>
            </Box>
          </Box>
        ))}

        <Box mt={5}>
          <ButtonComponent
            color="default"
            variant="contained"
            disabled={this.state.isProcessing}
            onClick={this.cancelInvoice}
          ><IntlMessageComponent id="page.sellInvoice.button.cancelInvoice" /></ButtonComponent>

          <Box component="span" ml={1}>
            <ButtonComponent
              color="primary"
              variant="contained"
              isProcessing={this.state.isProcessing}
              onClick={this.submit}
            ><IntlMessageComponent id="page.sellInvoice.tab2.button.validate" /></ButtonComponent>
          </Box>

          <MandatoryFormFieldsComponent />
        </Box>
      </React.Fragment>
    );
  }
}

export default SellInvoiceDocumentsBatchComponent;
